import React, { useState, useEffect, useRef } from "react";
import data from "./data/showcaseSectionData";
import Grid from "@mui/material/Grid";
import MKTypography from "components/MKTypography";
import Skeleton from "@mui/material/Skeleton";
import "./showcase.css";

import { motion } from "framer-motion";

// Section animation
const fadeInUpSection = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

function ShowcaseSections() {
  // Track which section is visible
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = entry.target.getAttribute("data-index");
            setVisibleSections((prev) => ({ ...prev, [idx]: true }));
          }
        });
      },
      { threshold: 0.2 }
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      sectionRefs.current.forEach((el) => {
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  return (
    <div className="showcase-wrapper">
      {data.map((section, i) => (
        <motion.section
          key={i}
          data-index={i}
          ref={(el) => (sectionRefs.current[i] = el)}
          className="showcase-section"
          initial="hidden"
          animate={visibleSections[i] ? "visible" : "hidden"}
          variants={fadeInUpSection}
        >
          {/* TITLE + DESCRIPTION */}
          <Grid
            container
            item
            xs={12}
            lg={6}
            flexDirection="column"
            alignItems="center"
            sx={{ textAlign: "center", my: 2, mx: "auto" }}
          >
            <MKTypography variant="h2" fontWeight="bold">
              {section.title}
            </MKTypography>
            <MKTypography variant="body1" color="text">
              {section.description}
            </MKTypography>
          </Grid>

          {/* CARDS GRID */}
          <div className="showcase-grid">
            {section.items.map((item, j) => (
              <div key={j} className="showcase-card">
                {visibleSections[i] ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    decoding="async"
                    className="showcase-img"
                  />
                ) : (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={180}
                    animation="wave"
                  />
                )}

                <h4 className="showcase-name">{item.name}</h4>
                {item.bio && <p className="showcase-bio">{item.bio}</p>}
              </div>
            ))}
          </div>
        </motion.section>
      ))}
    </div>
  );
}

export default React.memo(ShowcaseSections);
